import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { JobTask_Entity } from "../../entities/jobTask";
import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";

const jobTasksAdapter = createEntityAdapter<JobTask_Entity>();
const initialState = jobTasksAdapter.getInitialState();

export const jobTaskEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getJobTasks: build.query<EntityState<JobTask_Entity>, void>({
      query: () => `jobTasks`,
      transformResponse: (responseData: JobTask_Entity[]) => {
        return jobTasksAdapter.setAll(initialState, responseData);
      },
      providesTags: ["jobTasks"],
    }),
    getJobTask: build.query<JobTask_Entity, number>({
      query: (id) => `jobTasks/${id}`,
      providesTags: (result, error, id) => [{ type: "jobTasks", id }],
    }),
    createJobTask: build.mutation<JobTask_Entity, Partial<JobTask_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `jobTasks`,
      }),
      invalidatesTags: ["jobTasks", "projectTasks", "budgets", "projects"],
    }),
    updateJobTask: build.mutation<
      void,
      { id: number; body: Partial<JobTask_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `jobTasks/${args.id}`,
      }),
      // Deliberately no invalidatesTags — refreshing per cell rebuilt the grid
      // mid-typing and dropped input. See scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, [
          "jobTasks",
          "projectTasks",
          "budgets",
          "projects",
        ]);
      },
    }),
    deleteJobTask: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `jobTasks/${id}`,
      }),
      invalidatesTags: ["jobTasks", "projectTasks", "budgets", "projects"],
    }),
  }),
});

export const {
  useGetJobTaskQuery,
  useGetJobTasksQuery,
  useCreateJobTaskMutation,
  useDeleteJobTaskMutation,
  useUpdateJobTaskMutation,
} = jobTaskEndpoints;

export default jobTaskEndpoints;
